import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { g, p, eur, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePlan } from "@/context/PlanContext";
import { useI18n } from "@/i18n";
import { PremiumLock } from "@/components/PremiumLock";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Banknote, Wallet, Clock, Calculator, HandCoins, Printer } from "lucide-react";

export default function Cassa() {
  const { user, settings } = useApp();
  const { has } = usePlan();
  const { t, lang } = useI18n();
  const [rep, setRep] = useState(null);
  const [date, setDate] = useState("");
  const [count, setCount] = useState({ counted: "", float_cash: "" });
  const [handIn, setHandIn] = useState({});
  const [floatAll, setFloatAll] = useState("");
  // Un cameriere vede solo i propri incassi e il proprio fondo: i colleghi non sono affar suo.
  const soloMio = !(user?.role === "titolare" || user?.role === "direttore");

  const load = useCallback(async () => {
    if (!has("cash_report")) return;
    try {
      setRep(await g("/cash/report", { ...(date ? { date } : {}), ...(soloMio ? { mine: user.name } : {}) }));
    } catch (e) {
      console.warn("Report di cassa non disponibile (piano o rete):", e?.response?.status || e?.message);
    }
  }, [has, date, soloMio, user?.name]);
  useEffect(() => {
    load();
  }, [load]);

  if (!has("cash_report"))
    return (
      <PremiumLock
        title={t("x.cash.title")}
        description={t("x.cash.perMethod")}
        needed="standard"
        message={t("x.cash.title")}
        preview={
          <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-3">
            {[[t("x.cash.sold"), "1.240,00 €"], [t("x.cash.cashed"), "1.180,00 €"], [t("x.cash.pendingTotal"), "60,00 €"]].map(([l, v]) => (
              <Card key={l} className="p-5">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{l}</p>
                <p className="font-num mt-3 text-2xl font-bold">{v}</p>
              </Card>
            ))}
          </div>
        }
      />
    );

  const hm = (s) => new Date(s).toLocaleTimeString(lang, { hour: "2-digit", minute: "2-digit" });
  const expected = (rep?.cash || 0) + (Number(count.float_cash) || 0);
  const diff = (Number(count.counted) || 0) - expected;

  const confirm = async (row) => {
    try {
      await p(`/orders/${row.order_id}/payments/${row.payment_id}/confirm`, { by: user.name });
      toast.success(t("x.cash.confirmed"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const sendHandIn = async (f) => {
    const amount = Number(handIn[f.person] ?? 0) || Math.max(f.to_hand_in, 0);
    if (amount <= 0) return toast.error(t("x.cash.handInAmount"));
    try {
      await p("/cash/handin", { by: f.person, amount, to: user.name });
      setHandIn({ ...handIn, [f.person]: "" });
      toast.success(t("x.cash.handInOk"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const assignFloats = async () => {
    try {
      const r = await p("/cash/floats", { amount: Number(floatAll) || 0 });
      toast.success(t("x.cash.setFloatsOk", { n: r.updated }));
      setFloatAll("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const printClosing = () => {
    if (!rep) return;
    const w = window.open("", "_blank", "width=520,height=720");
    if (!w) return toast.error(t("x.cash.popups"));
    const day = new Date(rep.from).toLocaleDateString(lang);
    const zoneRows = (rep.per_zone || [])
      .map((z) => `<tr><td>${z.zone}</td><td style="text-align:right">${eur(z.cash)}</td><td style="text-align:right">${eur(z.total - z.cash)}</td><td style="text-align:right"><b>${eur(z.total)}</b></td></tr>`)
      .join("");
    const methodRows = (rep.per_method || [])
      .map((m) => `<tr><td style="text-transform:capitalize">${m.method}</td><td style="text-align:right">${eur(m.amount)}</td></tr>`)
      .join("");
    const floatRows = (rep.floats || [])
      .map((f) => `<tr><td>${f.person}</td><td style="text-align:right">${eur(f.float_cash)}</td><td style="text-align:right">${eur(f.cash)}</td><td style="text-align:right">${eur(f.handed_in)}</td><td style="text-align:right"><b>${eur(Math.max(f.to_hand_in, 0))}</b></td></tr>`)
      .join("");
    const personRows = (rep.per_person || [])
      .map((p2) => `<tr><td>${p2.person}</td><td style="text-align:right">${eur(p2.total)}</td></tr>`)
      .join("");
    w.document.write(`<html><head><title>${t("x.cash.closing")}</title></head>
      <body style="font-family:Helvetica,Arial,sans-serif;font-size:12px;padding:18px">
      ${settings.logo ? `<p style="text-align:center"><img src="${settings.logo}" style="max-height:56px"/></p>` : ""}
      <h2 style="text-align:center;margin:4px 0">${settings.business_name}</h2>
      ${settings.motto && settings.motto_on_bill !== false ? `<p style="text-align:center;font-style:italic;margin:0">«${settings.motto}»</p>` : ""}
      <p style="text-align:center;margin:6px 0 14px">${t("x.cash.closing")} · ${day} · ${t("x.cash.countedBy", { name: user.name })}</p>
      <table style="width:100%;border-collapse:collapse">
        <tr><td>${t("x.cash.sold")}</td><td style="text-align:right"><b>${eur(rep.sold)}</b></td></tr>
        <tr><td>${t("x.cash.cashed")}</td><td style="text-align:right"><b>${eur(rep.cashed)}</b></td></tr>
        <tr><td>${t("x.cash.pendingTotal")}</td><td style="text-align:right">${eur(rep.pending_total)}</td></tr>
      </table>
      <h3 style="margin:16px 0 4px">${t("x.cash.perZone")}</h3>
      <table style="width:100%;border-collapse:collapse">
        <tr style="border-bottom:1px solid #999"><th style="text-align:left">${t("x.cash.perZone")}</th><th style="text-align:right">${t("x.cash.cashWord")}</th><th style="text-align:right">${t("x.cash.otherWord")}</th><th style="text-align:right">${t("b.total")}</th></tr>
        ${zoneRows || `<tr><td colspan="4">${t("x.cash.noRows")}</td></tr>`}
      </table>
      <h3 style="margin:16px 0 4px">${t("x.cash.perMethod")}</h3>
      <table style="width:100%;border-collapse:collapse">${methodRows || `<tr><td>${t("x.cash.noRows")}</td></tr>`}</table>
      <h3 style="margin:16px 0 4px">${t("x.cash.floats")}</h3>
      <table style="width:100%;border-collapse:collapse">
        <tr style="border-bottom:1px solid #999"><th style="text-align:left">${t("x.cash.perPerson")}</th><th style="text-align:right">${t("x.cash.float")}</th><th style="text-align:right">${t("x.cash.cashWord")}</th><th style="text-align:right">${t("x.cash.handedIn")}</th><th style="text-align:right">${t("x.cash.toHandIn")}</th></tr>
        ${floatRows || `<tr><td colspan="5">${t("x.cash.floatsNone")}</td></tr>`}
      </table>
      <h3 style="margin:16px 0 4px">${t("x.cash.perPerson")}</h3>
      <table style="width:100%;border-collapse:collapse">${personRows || `<tr><td>${t("x.cash.noRows")}</td></tr>`}</table>
      <p style="margin-top:24px">${t("x.cash.signature")} __________________________</p>
      <p style="text-align:center;font-size:10px;color:#666">${t("b.notFiscal")}</p>
      </body></html>`);
    w.document.close();
    w.print();
  };

  const saveCount = async () => {
    try {
      await p("/cash/count", {
        counted: Number(count.counted) || 0,
        float_cash: Number(count.float_cash) || 0,
        by: user.name,
      });
      setCount({ counted: "", float_cash: "" });
      toast.success(t("x.cash.countOk"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="fade-up space-y-5" data-testid="cassa-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">{t("x.cash.overline")}</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">{t("x.cash.title")}</h1>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">{t("x.cash.date")}</Label>
            <Input type="date" className="mt-1 h-10 w-44" value={date} onChange={(e) => setDate(e.target.value)} data-testid="cash-date-input" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {(soloMio
          ? [["Incassato da te", eur(rep?.cashed), Banknote],
             [t("x.cash.pendingTotal"), eur(rep?.pending_total), Clock]]
          : [
            [t("x.cash.sold"), eur(rep?.sold), Wallet],
            [t("x.cash.cashed"), eur(rep?.cashed), Banknote],
            [t("x.cash.pendingTotal"), eur(rep?.pending_total), Clock],
          ]).map(([l, v, Icon]) => (
          <Card key={l} className="p-5" data-testid={`cash-kpi-${l.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="flex items-start justify-between">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{l}</p>
              <Icon className="h-4 w-4 text-slate-400" />
            </div>
            <p className="font-num mt-3 text-2xl font-bold">{v}</p>
          </Card>
        ))}
      </div>

      {rep?.pending?.length > 0 && (
        <Card className="border-2 border-amber-400 bg-amber-50 p-5" data-testid="cash-pending-panel">
          <h3 className="text-lg font-semibold">{t("x.cash.pendingTotal")}</h3>
          <div className="mt-3 space-y-2">
            {rep.pending.map((r) => (
              <div key={r.payment_id} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-white p-3" data-testid={`pending-${r.payment_id}`}>
                <span className="text-sm">
                  {r.table} · <span className="font-num font-bold">{eur(r.amount)}</span> · {r.from} → {r.by}
                </span>
                <Button size="sm" data-testid={`confirm-cash-${r.payment_id}`} onClick={() => confirm(r)}>
                  {t("x.cash.confirm")}
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className={soloMio ? "hidden" : "p-5"} data-testid="cash-per-zone">
          <h3 className="text-lg font-semibold">{t("x.cash.perZone")}</h3>
          <div className="mt-3 space-y-2 text-sm">
            {(rep?.per_zone || []).length === 0 && <p className="text-slate-500">{t("x.cash.noRows")}</p>}
            {(rep?.per_zone || []).map((z) => (
              <div key={z.zone} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0" data-testid={`zone-total-${z.zone}`}>
                <span>
                  {z.zone}
                  {z.cash > 0 && <span className="ml-1 text-[11px] text-slate-500">({t("x.cash.perMethod").split(",")[0].toLowerCase()} {eur(z.cash)})</span>}
                </span>
                <span className="font-num font-bold">{eur(z.total)}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5" data-testid="cash-per-person">
          <h3 className="text-lg font-semibold">{soloMio ? "I tuoi incassi" : t("x.cash.perPerson")}</h3>
          <div className="mt-3 space-y-2 text-sm">
            {(rep?.per_person || []).length === 0 && <p className="text-slate-500">{t("x.cash.noRows")}</p>}
            {(rep?.per_person || []).map((w) => (
              <div key={w.person} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0" data-testid={`person-${w.person}`}>
                <span>
                  {w.person}
                  {w.third_party > 0 && <span className="ml-1 text-[11px] text-slate-500">({t("x.cash.thirdParty")} {eur(w.third_party)})</span>}
                </span>
                <span className="font-num font-bold">{eur(w.total)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="p-5" data-testid="cash-per-method">
          <h3 className="text-lg font-semibold">{t("x.cash.perMethod")}</h3>
          <div className="mt-3 space-y-2 text-sm">
            {(rep?.per_method || []).length === 0 && <p className="text-slate-500">{t("x.cash.noRows")}</p>}
            {(rep?.per_method || []).map((m) => (
              <div key={m.method} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0" data-testid={`method-${m.method}`}>
                <span className="capitalize">{m.method}</span>
                <span className="font-num font-bold">{eur(m.amount)}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5" data-testid="cash-floats-panel">
          <h3 className="flex items-center gap-2 text-lg font-semibold">
            <HandCoins className="h-4 w-4" /> {soloMio ? "Il tuo fondo cassa" : t("x.cash.floats")}
          </h3>
          {(rep?.floats || []).length === 0 ? (
            <p className="mt-2 text-sm text-slate-500">{t("x.cash.floatsNone")}</p>
          ) : (
            <>
              <div className={soloMio ? "hidden" : "mt-3 flex flex-wrap gap-4 text-xs text-slate-500"}>
                <span>{t("x.cash.floatsTotal")}: <span className="font-num font-bold text-slate-900">{eur(rep.floats_total)}</span></span>
                <span>{t("x.cash.toHandInTotal")}: <span className="font-num font-bold text-slate-900">{eur(rep.to_hand_in_total)}</span></span>
              </div>
              <div className="mt-3 space-y-3">
                {rep.floats.map((f) => (
                  <div key={f.person} className="rounded-lg border border-slate-200 p-3" data-testid={`float-${f.person}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold">{f.person}</p>
                      <p className="font-num text-sm font-bold">
                        {eur(f.in_hand)} <span className="text-[11px] font-normal text-slate-500">{t("x.cash.inHand")}</span>
                      </p>
                    </div>
                    <p className="mt-0.5 text-[11px] text-slate-500">
                      {t("x.cash.float")} {eur(f.float_cash)} · {t("x.cash.handedIn")} {eur(f.handed_in)} ·{" "}
                      <span className={f.to_hand_in > 0 ? "font-bold text-amber-700" : ""}>
                        {t("x.cash.toHandIn")} {eur(Math.max(f.to_hand_in, 0))}
                      </span>
                    </p>
                    <div className="mt-2 flex gap-2">
                      <Input
                        className="h-9 w-32"
                        type="number"
                        step="0.01"
                        placeholder="€"
                        value={handIn[f.person] ?? ""}
                        onChange={(e) => setHandIn({ ...handIn, [f.person]: e.target.value })}
                        data-testid={`handin-input-${f.person}`}
                      />
                      <Button size="sm" variant="outline" data-testid={`handin-btn-${f.person}`} onClick={() => sendHandIn(f)}>
                        {t("x.cash.handIn")}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          <div className={soloMio ? "hidden" : "mt-4 flex flex-wrap items-end gap-2 border-t border-slate-100 pt-3"}>
            <div>
              <Label className="text-xs">{t("x.cash.float")}</Label>
              <Input className="mt-1 h-10 w-28" type="number" step="0.01" value={floatAll}
                     onChange={(e) => setFloatAll(e.target.value)} data-testid="float-all-input" />
            </div>
            <Button variant="outline" className="h-10" onClick={assignFloats} data-testid="float-all-btn">
              {t("x.cash.setFloats")}
            </Button>
          </div>
        </Card>
      </div>

      <Card className={soloMio ? "hidden" : "p-5"} data-testid="cash-count-panel">
        <h3 className="flex items-center gap-2 text-lg font-semibold">
          <Calculator className="h-4 w-4" /> {t("x.cash.count")}
        </h3>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <Label className="text-xs">{t("x.cash.counted")}</Label>
            <Input className="mt-1 h-11" type="number" step="0.01" value={count.counted} onChange={(e) => setCount({ ...count, counted: e.target.value })} data-testid="counted-input" />
          </div>
          <div>
            <Label className="text-xs">{t("x.cash.float")}</Label>
            <Input className="mt-1 h-11" type="number" step="0.01" value={count.float_cash} onChange={(e) => setCount({ ...count, float_cash: e.target.value })} data-testid="float-input" />
          </div>
          <div className="rounded-md border border-slate-200 p-3">
            <p className="text-[11px] text-slate-500">{t("x.cash.registered")} <span className="font-num font-bold">{eur(rep?.cash)}</span></p>
            <p className="text-[11px] text-slate-500">{t("x.cash.expected")} <span className="font-num font-bold">{eur(expected)}</span></p>
            <p className={`font-num mt-1 text-sm font-bold ${diff === 0 ? "text-slate-700" : diff < 0 ? "text-rose-600" : "text-emerald-600"}`} data-testid="cash-difference">
              {t("x.cash.diff")}: {diff > 0 ? "+" : ""}{eur(diff)}
            </p>
          </div>
        </div>
        <Button className="mt-3 h-11 w-full sm:w-auto" onClick={saveCount} disabled={count.counted === ""} data-testid="save-count-btn">
          {t("x.cash.saveCount")}
        </Button>
        <Button variant="outline" className="mt-3 h-11 w-full sm:ml-2 sm:w-auto" onClick={printClosing} data-testid="print-closing-btn">
          <Printer className="mr-1.5 h-4 w-4" /> {t("x.cash.printClosing")}
        </Button>
        {(rep?.counts || []).length > 0 && (
          <div className="mt-4 space-y-1 text-xs text-slate-500">
            {rep.counts.map((c) => (
              <p key={c.id} data-testid={`count-row-${c.id}`}>
                {hm(c.at)} · <span className="font-num">{eur(c.counted)}</span> · {t("x.cash.diff")}{" "}
                <span className={c.difference === 0 ? "" : "font-bold text-rose-600"}>{eur(c.difference)}</span> · {t("x.cash.countedBy", { name: c.by })}
              </p>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5" data-testid="cash-movements">
        <h3 className="text-lg font-semibold">{t("x.cash.movements")}</h3>
        {(rep?.rows || []).length === 0 && <p className="mt-2 text-sm text-slate-500">{t("x.cash.noRows")}</p>}
        <div className="mt-3 divide-y divide-slate-100">
          {(rep?.rows || []).map((r) => (
            <div key={r.payment_id} className="flex items-center justify-between gap-3 py-2 text-sm" data-testid={`cash-row-${r.payment_id}`}>
              <div className="min-w-0">
                <p className="truncate font-medium">{r.table}</p>
                <p className="text-[11px] text-slate-500">
                  {hm(r.at)} · {r.waiter} → {r.by} · {r.method}
                  {r.pending && <span className="ml-1 font-bold text-amber-700">{t("x.hand.pending", { name: r.by })}</span>}
                </p>
              </div>
              <span className={`font-num shrink-0 font-bold ${r.pending ? "text-amber-700" : ""}`}>{eur(r.amount)}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
